import { useState } from 'react';
import type { GatePage as GatePageConfig, RegistrationStatus, SurveyConfig } from '../types';
import { Blocks } from '../components/Blocks';
import { ReferralBlock } from '../components/ReferralBlock';
import { optOutSurvey } from '../api';
import { interpolateBlocks } from '../lib/interpolate';
import { buildEventVars } from '../lib/stepTwo';
import { resolveTierLine } from '../lib/upgradeStep';

interface Props {
  config: GatePageConfig;
  onContinue: () => void;
  /** When non-null, the user arrived via resume-token (widget registration).
   *  Drives status-aware copy + survey opt-out affordances. */
  registrationStatus?: RegistrationStatus | null;
  surveyConfig?: SurveyConfig;
  /** Submission ID from widget-based registration — enables opt-out button. */
  resumedSubmissionId?: string;
  onSurveyOptOut?: (status: RegistrationStatus) => void;
  leadFirstName?: string | null;
  /** Pre-resolved event template vars from sanitizeConfigForPublic (eventDuration, eventDayLong, etc.).
   *  Used to interpolate {{eventDuration}} etc. inside gate-page blocks. */
  eventConfig?: Record<string, unknown>;
  /** TSB-Member auto-VIP grant, known at resume time. Hides config.upgradeBlocks
   *  when true — someone already on a paid tier shouldn't see an upgrade pitch.
   *  See App.tsx's memberVip doc comment for what this signal does and doesn't
   *  cover (a separately-purchased paid tier is a known, accepted gap). */
  memberVip?: boolean;
  /** The resumed registration's tier and its source (resume response). With
   *  config.tierLines, a buyer sees "Your VIP pass is confirmed" — see
   *  lib/upgradeStep.ts resolveTierLine. */
  tierKey?: string | null;
  tierSource?: string | null;
}

/**
 * Mandatory interstitial between email gate and questions.
 *
 * Renders three distinct variants depending on registrationStatus:
 *  - null (no resume token) OR 'registered-pending-survey' → standard gate content + proceed CTA
 *  - 'registered-survey-complete' → terminal "you're all set" confirmation
 *  - 'registered-survey-opted-out' → terminal "reminders muted, see you at the event" confirmation
 *
 * For pending-survey + optional-survey, adds a secondary "Skip the survey" link.
 */
export default function GatePage({
  config,
  onContinue,
  registrationStatus,
  surveyConfig,
  resumedSubmissionId,
  onSurveyOptOut,
  leadFirstName,
  eventConfig,
  memberVip,
  tierKey,
  tierSource,
}: Props) {
  const [optOutPending, setOptOutPending] = useState(false);
  const [optOutError, setOptOutError] = useState<string | null>(null);

  const greeting = leadFirstName ? `, ${leadFirstName.split(' ')[0]}` : '';

  // Terminal states — user already past this gate, shouldn't go into the survey.
  if (registrationStatus === 'registered-survey-complete') {
    return (
      <div className="gate-page">
        <div className="card" style={{ textAlign: 'center', padding: '40px 32px' }}>
          <h1 style={{ marginTop: 0 }}>You're all set{greeting}.</h1>
          <p className="muted" style={{ fontSize: '1.05rem', lineHeight: 1.55 }}>
            You're registered and you've completed the survey. We'll send a confirmation
            and reminders as the event gets closer. Nothing else for you to do right now.
          </p>
        </div>
        <ReferralBlock submissionId={resumedSubmissionId} />
      </div>
    );
  }

  if (registrationStatus === 'registered-survey-opted-out') {
    return (
      <div className="gate-page">
        <div className="card" style={{ textAlign: 'center', padding: '40px 32px' }}>
          <h1 style={{ marginTop: 0 }}>You're registered{greeting}.</h1>
          <p className="muted" style={{ fontSize: '1.05rem', lineHeight: 1.55 }}>
            Survey reminders are off. We'll send event reminders as the event gets closer.
            See you there.
          </p>
        </div>
        <ReferralBlock submissionId={resumedSubmissionId} />
      </div>
    );
  }

  const resumedBanner = registrationStatus === 'registered-pending-survey' ? (
    <div
      style={{
        padding: '14px 18px',
        background: 'color-mix(in srgb, var(--color-success, #0F766E) 10%, transparent)',
        border: '1px solid color-mix(in srgb, var(--color-success, #0F766E) 25%, transparent)',
        borderRadius: 10,
        marginBottom: 24,
        fontSize: '0.95rem',
        lineHeight: 1.5,
      }}
    >
      <strong>You're registered{greeting}.</strong>{' '}
      {config.resumedBannerBody
        ? config.resumedBannerBody
        : surveyConfig?.required
          ? 'One quick step below to make sure you get the most out of the session.'
          : "Here's an optional short survey to personalise what you'll get."}
    </div>
  ) : null;

  const canOptOut =
    registrationStatus === 'registered-pending-survey' &&
    surveyConfig?.required === false &&
    !!resumedSubmissionId;

  async function handleOptOut() {
    if (!resumedSubmissionId) return;
    setOptOutPending(true);
    setOptOutError(null);
    try {
      const r = await optOutSurvey(resumedSubmissionId);
      onSurveyOptOut?.(r.status);
    } catch (err) {
      setOptOutError(err instanceof Error ? err.message : 'Could not opt out');
      setOptOutPending(false);
    }
  }

  // Template vars shared by `blocks` and `belowCtaBlocks`. {{firstName}} is
  // empty when unknown; {{greeting}} carries its own leading comma so
  // "You're in{{greeting}}." reads right either way.
  const vars = buildEventVars(eventConfig, leadFirstName);
  const tierLine = resolveTierLine(config, tierKey, tierSource);

  // Matches the same two cases this component's own docstring calls
  // "standard gate content" (null = no resume token / in-SPA registration,
  // 'registered-pending-survey' = widget/resume-token arrival) — the
  // terminal states (survey-complete/opted-out) return earlier and never
  // reach this line at all.
  // With config.upgradeStep the offer has its own screen before this one
  // (lib/upgradeStep.ts), so this page never repeats it.
  const showUpgrade =
    !config.upgradeStep &&
    !memberVip &&
    (registrationStatus === null || registrationStatus === 'registered-pending-survey') &&
    !!config.upgradeBlocks && config.upgradeBlocks.length > 0;

  return (
    <div className="gate-page">
      {resumedBanner}
      {tierLine && <div className="gate-tier-line" data-testid="gate-tier-line">{tierLine}</div>}
      <Blocks blocks={interpolateBlocks(config.blocks, vars)} />
      {showUpgrade && (
        <div className="gate-page-upgrade">
          <Blocks blocks={interpolateBlocks(config.upgradeBlocks!, vars)} />
        </div>
      )}
      <div style={{ textAlign: 'center', marginTop: 24 }}>
        <button id="gate-cta" className="btn btn-primary btn-xl" onClick={onContinue}>
          {config.ctaLabel}
        </button>
        {config.ctaNote && (
          <div className="gate-cta-note">{config.ctaNote}</div>
        )}
        {canOptOut && (
          <div style={{ marginTop: 18 }}>
            <button
              className="btn btn-link"
              onClick={handleOptOut}
              disabled={optOutPending}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--color-muted, #6B7280)',
                textDecoration: 'underline',
                cursor: optOutPending ? 'not-allowed' : 'pointer',
                fontSize: '0.95rem',
              }}
            >
              {optOutPending ? 'Saving…' : "Skip the survey — I'll just show up"}
            </button>
            {optOutError && (
              <div style={{ color: 'var(--color-error, #dc2626)', fontSize: '0.85rem', marginTop: 8 }}>
                {optOutError}
              </div>
            )}
          </div>
        )}
      </div>
      <ReferralBlock submissionId={resumedSubmissionId} />
      {config.belowCtaBlocks && config.belowCtaBlocks.length > 0 && (
        <div className="gate-page-below-cta">
          <Blocks blocks={interpolateBlocks(config.belowCtaBlocks, vars)} />
        </div>
      )}
    </div>
  );
}
